<template>
  <div class="modal-content">
    <form novalidate @submit.prevent="register" id="registration-modal-form">
      <ErrorMessage v-if="errorMessage" :error-message="errorMessage" />

      <div class="form-group">
        <Input
          ref="emailInput"
          type="email"
          placeholder="E-mail*"
          v-model.trim="email"
          :invalid="$v.email.$error"
          @keydown.space.prevent
        />
        <div
          v-if="$v.email.$dirty && !$v.email.required"
          class="invalid-feedback"
        >
          Поле обязательно для заполнения
        </div>
        <div
          v-else-if="$v.email.$dirty && !$v.email.email"
          class="invalid-feedback"
        >
          Некорректный E-mail
        </div>
      </div>

      <div class="form-group">
        <Input
          type="tel"
          placeholder="Ваш номер телефона"
          v-model="phone"
          :invalid="$v.phone.$error"
          :mask="{
            mask: '+ 375 (99) 999-99-99',
            placeholder: '_',
            showMaskOnHover: false,
          }"
        />
        <div
          v-if="$v.phone.$dirty && !$v.phone.required"
          class="invalid-feedback"
        >
          Поле обязательно для заполнения
        </div>
        <div
          v-else-if="$v.phone.$dirty && !$v.phone.validatePhone"
          class="invalid-feedback"
        >
          Некорректный номер телефона
        </div>
      </div>

      <div class="form-group-password">
        <Input
          :type="passwordFieldType"
          placeholder="Пароль*"
          v-model.trim="password"
          :invalid="$v.password.$error"
          @keydown.space.prevent
        />
        <button
          class="toggle-password-btn"
          :class="{ active: passwordFieldType === 'text' }"
          type="button"
          tabindex="-1"
          @click="togglePasswordVisibility"
        >
          <span v-if="passwordFieldType === 'password'" key="ico-visibility-off"
            ><ico name="icoVisibilityOff" color="#626262"></ico
          ></span>
          <span v-else key="ico-visibility"
            ><ico name="icoVisibility" color="#626262"></ico
          ></span>
        </button>

        <div
          v-if="$v.password.$dirty && !$v.password.required"
          class="invalid-feedback"
        >
          Поле обязательно для заполнения
        </div>
        <div
          v-else-if="$v.password.$dirty && !$v.password.minLength"
          class="invalid-feedback"
        >
          Пароль должен содержать не менее
          {{ $v.password.$params.minLength.min }} символов
        </div>
        <div
          v-else-if="$v.password.$dirty && !$v.password.strongPassword"
          class="invalid-feedback"
        >
          Пароль должен содержать минимум 1 цифру и одну заглавную букву
        </div>
      </div>

      <div class="form-group-password">
        <Input
          :type="confirmPasswordFieldType"
          placeholder="Повторите пароль*"
          v-model.trim="confirmPassword"
          :invalid="$v.confirmPassword.$error"
          @keydown.space.prevent
        />
        <button
          class="toggle-password-btn"
          type="button"
          tabindex="-1"
          @click="toggleConfirmPasswordVisibility"
        >
          <span
            v-if="confirmPasswordFieldType === 'password'"
            key="ico-visibility-off"
            ><ico name="icoVisibilityOff" color="#626262"></ico
          ></span>
          <span v-else key="ico-visibility"
            ><ico name="icoVisibility" color="#626262"></ico
          ></span>
        </button>

        <div
          v-if="$v.confirmPassword.$dirty && !$v.confirmPassword.required"
          class="invalid-feedback"
        >
          Поле обязательно для заполнения
        </div>
        <div
          v-else-if="
            $v.confirmPassword.$dirty && !$v.confirmPassword.sameAsPassword
          "
          class="invalid-feedback"
        >
          Пароли не совпадают
        </div>
      </div>

      <div class="form-group-checkboxes">
        <Checkbox
          v-model="confidentiality"
          :highlight-key="highlightConfidentialityKey"
        >
          <span @click.stop class="checkbox-text">
            Принимаю&nbsp;
            <a class="checkbox-link" :href="privacyPolicyUrl" target="_blank">
              Политику конфиденциальности
            </a>
          </span>
        </Checkbox>

        <Checkbox v-model="offer" :highlight-key="highlightOfferKey">
          <span @click.stop class="checkbox-text">
            Принимаю&nbsp;
            <a class="checkbox-link" :href="termOfUseUrl" target="_blank">
              Правила пользования
            </a>
          </span>
        </Checkbox>
      </div>

      <div class="form-footer">
        <Button
          class="button"
          variant="plain"
          type="button"
          @click="openLoginModal"
        >
          Войти
        </Button>

        <Button
          class="button"
          variant="gradient"
          type="submit"
          :title="isDisabled ? 'Заполните обязательные поля' : ''"
        >
          Зарегистрироваться
        </Button>
      </div>
    </form>
  </div>
</template>

<script>
import { email, minLength, required, sameAs } from "vuelidate/lib/validators";
import LoginModal from "@/components/homePage/LoginModal";
import SuccessModal from "@/components/common/SuccessModal";
import api from "../../application";
import {
  LoginModalProperties,
  SuccessModalProperties,
} from "@/utils/modalsProperties";
import { clearStorage } from "../../utils/auth";
import ErrorMessage from "@/components/common/ErrorMessage";
import Configuration from "@/configuration";
import Input from "@/components/homePage/Input";
import Button from "@/components/homePage/Button";
import Checkbox from "@/components/homePage/Checkbox";

export default {
  name: "RegistrationModal",
  data() {
    return {
      email: null,
      phone: null,
      password: null,
      passwordFieldType: "password",
      confirmPassword: null,
      confirmPasswordFieldType: "password",
      confidentiality: false,
      offer: false,
      errorMessage: null,
      showErrors: false,
      highlightConfidentialityKey: 0,
      highlightOfferKey: 0,
    };
  },
  components: {
    ErrorMessage,
    Input,
    Button,
    Checkbox,
  },
  validations: {
    email: { required, email },
    password: {
      required,
      minLength: minLength(8),
      strongPassword(password) {
        return /[A-ZА-Я]/.test(password) && /[0-9]/.test(password);
      },
    },
    phone: {
      required,
      validatePhone: function (phone) {
        return /\+ 375 \(\d\d\) \d\d\d-\d\d-\d\d/.test(phone);
      },
    },
    confirmPassword: { required, sameAsPassword: sameAs("password") },
  },
  computed: {
    isDisabled: function () {
      return (
        !this.email ||
        !this.phone ||
        !this.password ||
        !this.confirmPassword ||
        !this.confidentiality ||
        !this.offer
      );
    },
    termOfUseUrl() {
      return Configuration.value("apiEndpoint") + "/terms-of-use";
    },
    privacyPolicyUrl() {
      return Configuration.value("apiEndpoint") + "/privacy-policy";
    },
  },
  methods: {
    togglePasswordVisibility() {
      this.passwordFieldType =
        this.passwordFieldType === "password" ? "text" : "password";
    },
    toggleConfirmPasswordVisibility() {
      this.confirmPasswordFieldType =
        this.confirmPasswordFieldType === "password" ? "text" : "password";
    },
    openLoginModal() {
      this.$modal.show(LoginModal, null, LoginModalProperties);
    },
    openSuccessModal() {
      this.$modal.show(
        SuccessModal,
        {
          text: "Пожалуйста, проверьте Ваш почтовый ящик для&nbsp;завершения регистрации",
        },
        SuccessModalProperties,
      );
    },
    async register() {
      this.showErrors = true;
      this.$v.$touch();

      if (!this.confidentiality) this.highlightConfidentialityKey++;
      if (!this.offer) this.highlightOfferKey++;

      if (this.$v.$invalid || !this.confidentiality || !this.offer) return;
      this.errorText = null;
      const formData = {
        email: this.email,
        phone: this.phone,
        password: this.password,
      };

      try {
        await api.auth.registerUser(formData);
        clearStorage();
        this.openSuccessModal();
      } catch (e) {
        this.errorMessage = e.response.data.errorMessage;
      }
    },
  },
  mounted() {
    this.$refs["emailInput"].focus();
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/less/variables.less";

.modal-content {
  form {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }
}

.form-footer {
  flex-direction: column;
  padding: 0;
  .button {
    width: 100%;
    height: 60px;
  }
}

.form-group {
  margin-bottom: 0;
}

.form-control-phone {
  padding-left: 44px;
  background-repeat: no-repeat;
  background-position: 18px center;
  background-image: url("../../assets/img/ico-flag.svg");
  background-size: 18px auto;
}

.form-group-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .checkbox-text {
    font-family: Montserrat;
    font-weight: 500;
    font-size: 12px;
    line-height: 1;
    color: white;
    display: flex;
    align-items: center;
    .checkbox-link {
      text-decoration: none;
      color: #a5d8ff;
    }
  }
}
</style>
